/**
 * Standardized query key factories for Pinia Colada.
 *
 * Conventions:
 * - Keys are arrays starting with a feature prefix (e.g., 'sl', 'campaigns')
 * - Use `as const` for type safety and autocomplete
 * - Include ALL parameters that affect the query result
 * - Nested objects are compared by deep equality
 *
 * Benefits:
 * - Consistent cache key structure across the codebase
 * - Easy to invalidate related queries (e.g., all 'sl' queries)
 * - Type-safe key generation with autocomplete
 *
 * @see docs/pinia-and-pinia-colada-guidelines.md for usage guidance
 *
 * @example
 * // In a query definition
 * useQuery({
 *   key: () => QueryKeys.sl.topicStats(orgId, topicId, filters),
 *   query: () => fetchTopicStats(...)
 * })
 *
 * @example
 * // Invalidating all SL queries
 * queryCache.invalidateQueries({ key: QueryKeys.sl.all(), exact: false })
 */

import type { PerformanceAlertDetails } from '@dashhudson/api-client/library/model';

/**
 * Returns a new object with keys sorted alphabetically, so equivalent filter
 * payloads produce the same cache key regardless of property insertion order.
 * Undefined values are dropped to keep keys stable across optional params.
 */
function normalizeFilters<T extends Record<string, unknown>>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters)
      .filter(([, value]) => value !== undefined)
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}

/**
 * Sorted copy of a requested metric set. Two sections declaring the same metrics in a different
 * order describe the same request, so they must land on the same cache entry rather than fetching
 * twice. Copies first — the caller's array is config, not ours to reorder.
 */
function sortMetrics(metrics: string[]): string[] {
  return [...metrics].sort();
}

/**
 * Reporting window for an analytics overview query. The comparison half is optional — channels
 * without a comparison period must key the same either way, which `normalizeFilters` handles by
 * dropping the undefined entries.
 */
type OverviewKeyRange = {
  startDate: string;
  endDate: string;
  contextStartDate?: string;
  contextEndDate?: string;
};

export const QueryKeys = {
  /**
   * Auth query keys.
   * Scoped to the current authenticated user unless otherwise noted.
   */
  auth: {
    /** Root key for all auth queries */
    all: () => ['auth'] as const,

    /** Users across a set of brands (one batched request; key is the sorted brand-id set) */
    brandUsers: (brandIds: number[]) =>
      ['auth', 'brand-users', [...brandIds].sort((a, b) => a - b)] as const,

    /** One user scoped to a brand */
    brandUser: (brandId: number, userId: number) =>
      ['auth', 'brand-user', brandId, userId] as const,

    /** OAuth authorizations for the current user */
    oauthAuthorizations: () => ['auth', 'oauth-authorizations'] as const,
  },

  /**
   * Social Listening query keys.
   * All SL queries are scoped by organization ID.
   */
  sl: {
    /** Root key for all SL queries - use for broad invalidation */
    all: () => ['sl'] as const,
    /** Topics list for an organization */
    topics: (orgId: number) => ['sl', 'topics', orgId] as const,

    /** Single topic details */
    topic: (orgId: number, topicId: number) => ['sl', 'topic', orgId, topicId] as const,

    /** Topic statistics with filters */
    topicStats: (orgId: number, topicId: number, filters: Record<string, unknown>) =>
      ['sl', 'topic-stats', orgId, topicId, filters] as const,

    /** Search results */
    search: (orgId: number, filters: Record<string, unknown>) =>
      ['sl', 'search', orgId, filters] as const,

    /** Parsed boolean search expression */
    parseTree: (orgId: number | null, expression: string) =>
      ['sl', 'parse-tree', orgId, expression] as const,

    /** Volume/trends data */
    volume: (orgId: number, topicId: number, params: Record<string, unknown>) =>
      ['sl', 'volume', orgId, topicId, params] as const,
  },

  /**
   * Creators query keys.
   * Most creator queries are scoped by brand ID.
   */
  creators: {
    /** Root key for all creator queries */
    all: () => ['creators'] as const,

    /** Influential fans with optional source and sort */
    influentialFans: (brandId: number, source?: string, sort?: string) =>
      ['creators', 'influential-fans', brandId, source, sort] as const,

    /** AI insights for a specific creator */
    aiInsights: (brandId: number, creatorId: number) =>
      ['creators', 'ai-insights', brandId, creatorId] as const,

    /** Creator details */
    detail: (brandId: number, creatorId: number) =>
      ['creators', 'detail', brandId, creatorId] as const,

    /** Single creator-profile read, scoped by optional metric filters (date range / post type) */
    profile: (creatorId: number, params: object = {}) =>
      ['creators', 'profile', creatorId, JSON.stringify(params)] as const,

    /** Prefix for all profile reads of a creator (cache walk / invalidation) */
    profileRoot: (creatorId: number) => ['creators', 'profile', creatorId] as const,

    /** Paginated "Your Creators" list for a brand, scoped by search params */
    yourCreators: (brandId: number, params: object) =>
      ['creators', 'your-creators', brandId, JSON.stringify(params)] as const,

    /** Prefix for all "Your Creators" list pages of a brand (cache walk / invalidation) */
    yourCreatorsRoot: (brandId: number) => ['creators', 'your-creators', brandId] as const,

    /** Count of saved creators for a brand (plan-limit progress bar) */
    count: (brandId: number) => ['creators', 'count', brandId] as const,

    /** Specific creators resolved by id (one-shot fetch) */
    byIds: (brandId: number, creatorIds: number[]) =>
      ['creators', 'by-ids', brandId, creatorIds] as const,

    /** Full payee roster for a brand, drained page-by-page */
    payees: (brandId: number) => ['creators', 'payees', brandId] as const,

    /** Single payee by Trolley recipient id */
    payee: (brandId: number, payeeId: string) => ['creators', 'payee', brandId, payeeId] as const,

    /** Social account profile (IG or TikTok) */
    account: (brandId: number, source: string, handle: string, sourceCreatorId: string | null) =>
      ['creators', 'account', brandId, source, handle, sourceCreatorId] as const,

    /** RIQ "posts featuring you" media list (infinite, URL-paged) */
    riqPosts: (brandId: number, source: string, params: Record<string, unknown>) =>
      ['creators', 'riq-posts', brandId, source, params] as const,

    /** Account performance metrics */
    accountMetrics: (brandId: number, source: string, handle: string) =>
      ['creators', 'account-metrics', brandId, source, handle] as const,

    /** Avg weekly posts (slow endpoint, queried separately so it never blocks metrics) */
    accountAvgWeeklyPosts: (brandId: number, source: string, handle: string) =>
      ['creators', 'account-avg-weekly-posts', brandId, source, handle] as const,
  },

  /**
   * Campaigns query keys.
   * Most campaign queries are scoped by brand ID and/or campaign ID.
   */
  campaigns: {
    /** Root key for all campaign queries */
    all: () => ['campaigns'] as const,

    /** Campaigns list for a brand */
    list: (brandId: number) => ['campaigns', 'list', brandId] as const,

    /** Single campaign details */
    detail: (brandId: number, campaignId: number) =>
      ['campaigns', 'detail', brandId, campaignId] as const,

    /** Campaign deliverables with optional params */
    deliverables: (brandId: number, campaignId: number, params?: Record<string, unknown>) =>
      ['campaigns', 'deliverables', brandId, campaignId, params] as const,

    /** Campaign stats */
    stats: (brandId: number, campaignId: number, params?: Record<string, unknown>) =>
      ['campaigns', 'stats', brandId, campaignId, params] as const,

    /** Ad campaigns */
    adCampaigns: (brandId: number, campaignId: number) =>
      ['campaigns', 'ad-campaigns', brandId, campaignId] as const,

    /** Campaign payments */
    payments: (brandId: number, campaignId: number) =>
      ['campaigns', 'payments', brandId, campaignId] as const,

    /**
     * Campaigns fetched by ID — non-paginated bare-array response.
     * `ids` is sorted so equivalent selections share a cache entry.
     */
    byIds: (brandId: number, ids: readonly number[]) =>
      ['campaigns', 'by-ids', brandId, [...ids].sort((a, b) => a - b)] as const,
  },

  /**
   * Creator gifting query keys. Gifting data is scoped to a brand + campaign.
   */
  creatorGifting: {
    /** Root key for all creator-gifting queries */
    all: () => ['creator-gifting'] as const,

    /** Gifting collections for a campaign */
    list: (brandId: number, campaignId: number) =>
      ['creator-gifting', 'list', brandId, campaignId] as const,

    /** Campaign gifting settings (budget/deadline/orders-open) */
    giftDetail: (brandId: number, campaignId: number) =>
      ['creator-gifting', 'gift-detail', brandId, campaignId] as const,

    /** Every campaign creator with their gifting order */
    orders: (brandId: number, campaignId: number) =>
      ['creator-gifting', 'orders', brandId, campaignId] as const,
  },

  /**
   * Scheduler query keys.
   */
  scheduler: {
    /** The active user's shareable calendar links */
    shareableCalendarLinks: () => ['scheduler', 'shareable-calendar-links'] as const,

    /** The brand's Publishing Autopilot web article tracker (domain + install snippet) */
    webArticleTracker: (brandId: number | null | undefined) =>
      ['scheduler', 'web-article-tracker', brandId ?? null] as const,

    /** The brand's Publishing Autopilot content rules */
    autopilotRules: (brandId: number | null | undefined) =>
      ['scheduler', 'autopilot-rules', brandId ?? null] as const,

    /** The brand's scheduler settings (queue pause state) */
    queueSettings: (brandId: number | null | undefined) =>
      ['scheduler', 'queue-settings', brandId ?? null] as const,
  },

  /**
   * Instagram query keys.
   * Scoped by brand ID.
   */
  instagram: {
    /** Root key for all Instagram queries */
    all: () => ['instagram'] as const,

    /** Demographics insights */
    demographics: (brandId: number, params: Record<string, unknown>) =>
      ['instagram', 'demographics', brandId, params] as const,

    /** Account insights */
    insights: (brandId: number, params: Record<string, unknown>) =>
      ['instagram', 'insights', brandId, params] as const,

    /** Single Instagram post (refreshed metrics for the media popup) */
    post: (brandId: number | null | undefined, postSourceId: string | number | null | undefined) =>
      ['instagram', 'post', brandId ?? null, postSourceId ?? null] as const,
  },

  /**
   * Pinterest query keys.
   * Scoped by brand ID.
   */
  pinterest: {
    /** Root key for all Pinterest queries */
    all: () => ['pinterest'] as const,

    /** Engagement page chart data (all five metrics, one date-range/scale combo) */
    engagementGraphStats: (
      brandId: number | null | undefined,
      startDate: string,
      endDate: string,
      timeScale: string,
    ) =>
      [
        'pinterest',
        'engagement-graph-stats',
        brandId ?? null,
        startDate,
        endDate,
        timeScale,
      ] as const,

    /**
     * Top pins for a window. Outside `QueryKeys.dashboards` because pinterest-backend serves it,
     * not the dashboards Content endpoint — the reporting and comparison windows are two entries
     * under this one factory rather than one entry carrying both.
     */
    topPins: (
      brandId: number | null | undefined,
      params: { startDate: string; endDate: string; sort: string; limit: number },
    ) => ['pinterest', 'top-pins', brandId ?? null, normalizeFilters(params)] as const,

    /** Net new / total follower timeseries for the Your Followers page */
    followerGraphStats: (
      brandId: number,
      params: { startDate: string; endDate: string; scale: string },
    ) => ['pinterest', 'follower-graph-stats', brandId, normalizeFilters(params)] as const,
  },

  /**
   * Library/Media query keys.
   * Scoped by brand ID.
   */
  library: {
    /** Root key for all library queries */
    all: () => ['library'] as const,

    /** Root for every media list of a brand — use as an invalidation prefix */
    mediaRoot: (brandId: number) => ['library', 'media', brandId] as const,

    /** Media list with filters */
    media: (brandId: number, params: Record<string, unknown>) =>
      ['library', 'media', brandId, params] as const,

    /** Top post media list with filters and sorting */
    topPostMediaList: <TFilters>(
      brandId: number | null,
      params: {
        limit: number;
        filters: TFilters | null;
        sortFields: string[] | null;
      },
    ) => ['library', 'top-post-media-list', brandId, params] as const,

    /** UGC per-rights-status counts for a given filter set (excludes contentRightsType) */
    contentRightsCounts: (brandId: number, filters: Record<string, unknown>) =>
      ['library', 'content-rights-counts', brandId, filters] as const,

    /** Galleries for a brand, scoped by gallery type — use as invalidation prefix */
    galleries: (brandId: number, galleryType: string) =>
      ['library', 'galleries', brandId, galleryType] as const,

    /** Galleries scoped by gallery type and search query — use as the query key */
    galleriesSearch: (brandId: number, galleryType: string, search: string) =>
      ['library', 'galleries', brandId, galleryType, search] as const,

    /**
     * Root for the paginated gallery list family and the by-ids lookup.
     * Use as an invalidation prefix on brand change to clear all migrated
     * gallery list/by-ids caches scoped to the previous brand.
     */
    galleriesListRoot: (brandId: number) => ['library', 'gallery-lists', brandId] as const,

    /**
     * Paginated gallery list with the full filter set (default scope).
     * `filters` is normalized so equivalent payloads share a cache entry.
     */
    galleriesList: (brandId: number, filters: Record<string, unknown>) =>
      ['library', 'gallery-lists', brandId, 'list', normalizeFilters(filters)] as const,

    /**
     * Paginated gallery list scoped to the board-index page.
     * Isolated from `galleriesList` so the board-index pagination cursor
     * does not interleave with menu/dropdown gallery list caches.
     */
    galleriesPageList: (brandId: number, filters: Record<string, unknown>) =>
      ['library', 'gallery-lists', brandId, 'page', normalizeFilters(filters)] as const,

    /**
     * Galleries fetched by ID — non-paginated bare-array response.
     * `ids` is sorted so equivalent selections share a cache entry.
     */
    galleriesByIds: (brandId: number, ids: readonly number[]) =>
      ['library', 'gallery-lists', brandId, 'by-ids', [...ids].sort((a, b) => a - b)] as const,

    /** Root key for all single-gallery queries — use for broad invalidation */
    galleriesRoot: (brandId: number) => ['library', 'gallery', brandId] as const,

    /** Single gallery detail */
    gallery: (brandId: number, galleryId: number) =>
      ['library', 'gallery', brandId, galleryId] as const,

    /** Summary galleries (bare array, summary=true) for a brand, keyed by filter payload */
    galleriesSummary: (brandId: number, filters: Record<string, unknown>) =>
      ['library', 'galleries-summary', brandId, filters] as const,

    /** Root key for all summary gallery queries — use as invalidation prefix */
    galleriesSummaryAll: () => ['library', 'galleries-summary'] as const,

    /** Root key for all gallery-media queries in a brand — use for broad invalidation */
    galleryMediaRoot: (brandId: number) => ['library', 'gallery-media', brandId] as const,

    /** Gallery media v2 (paginated infinite query) */
    galleryMedia: (
      brandId: number,
      galleryId: number | string,
      filters?: Record<string, unknown>,
    ) =>
      filters
        ? (['library', 'gallery-media', brandId, galleryId, normalizeFilters(filters)] as const)
        : (['library', 'gallery-media', brandId, galleryId] as const),

    /**
     * Multi-brand gallery search results keyed by brand ID.
     * `brandIds` is sorted so equivalent selections share a cache entry.
     * `filters` is normalized for stable key comparison.
     */
    galleriesMultiBrand: (brandIds: number[], filters: Record<string, unknown>) =>
      [
        'library',
        'galleries-multi-brand',
        [...brandIds].sort((a, b) => a - b),
        normalizeFilters(filters),
      ] as const,

    /** Root key for all gallery-tag list queries — use as invalidation prefix */
    galleryTagsRoot: (brandId: number) => ['library', 'gallery-tags', brandId] as const,

    /**
     * Gallery tags for a brand, keyed by request params (activeOnly/galleryType/sort).
     * `params` is normalized so equivalent payloads share a cache entry.
     */
    galleryTags: (brandId: number, params: Record<string, unknown>) =>
      ['library', 'gallery-tags', brandId, normalizeFilters(params)] as const,

    /** Media links (media products) attached to a single media item */
    mediaLinks: (brandId: number, mediaId: number | string) =>
      ['library', 'media-links', brandId, mediaId] as const,

    /** UTM settings for a brand, scoped by channel */
    utmSettings: (brandId: number, channel: string) =>
      ['library', 'utm-settings', brandId, channel] as const,

    /** Open Graph metadata for a specific URL */
    urlMetadata: (url: string) => ['library', 'url-metadata', url] as const,

    /** Performance alerts for an organization */
    performanceAlerts: (orgId: number) => ['library', 'performance-alerts', orgId] as const,

    /**
     * Performance-alert-eligible metrics (library-backend), keyed by whether discontinued metrics
     * are included and the (sorted) selected brand IDs. Brand IDs drive server-side feature-flag
     * gating, so the query refetches when the selection changes. Not scoped by org — the catalog
     * doesn't vary by organization.
     */
    eligiblePerformanceAlertMetrics: (includeDeprecated: boolean, brandIds: number[] = []) =>
      ['library', 'performance-alert-eligible-metrics', includeDeprecated, brandIds] as const,

    /** Preview match results for a performance alert configuration */
    performanceAlertPreview: (
      orgId: number,
      brandIds: number[],
      details: Pick<PerformanceAlertDetails, 'thresholds' | 'filters'>,
    ) => ['library', 'performance-alerts-preview', orgId, brandIds, details] as const,

    /** Live galleries stats for a brand, keyed by request params */
    liveGalleriesStats: (brandId: number, params: Record<string, unknown>) =>
      ['library', 'live-galleries-stats', brandId, params] as const,

    /** Root key for all collaborator typeahead searches — cancel/invalidation prefix */
    collaboratorsRoot: () => ['library', 'collaborators'] as const,

    /**
     * Collaborator typeahead search (multi-brand). `brandIds` is sorted and
     * `dateRange` normalized so equivalent selections share a cache entry.
     */
    collaboratorSearch: (
      brandIds: number[],
      searchQuery: string,
      dateRange: { startDate?: string; endDate?: string } = {},
    ) =>
      [
        'library',
        'collaborators',
        'search',
        [...brandIds].sort((a, b) => a - b),
        searchQuery,
        normalizeFilters(dateRange),
      ] as const,

    /** Single-brand collaborator wildcard search */
    collaboratorWildcardSearch: (brandId: number, searchQuery: string) =>
      ['library', 'collaborators', 'wildcard-search', brandId, searchQuery] as const,
  },

  /** Library intelligence (vision service) query keys, scoped by brand ID. */
  libraryIntelligence: {
    /** Root key for all library intelligence queries */
    all: () => ['library-intelligence'] as const,

    /** Average star improvement for a brand over the last year */
    starImprovement: (brandId: number | null) =>
      ['library-intelligence', 'star-improvement', brandId] as const,
  },

  /**
   * Constellation Pro query keys.
   * Scoped by brand-level identifiers.
   */
  constellationPro: {
    /** Root key for all Constellation Pro queries */
    all: () => ['constellation-pro'] as const,

    /** Brand Guidelines record (assets + additional instructions) for a brand */
    brandGuidelines: (brandId: number | null) =>
      ['constellation-pro', 'brand-guidelines', brandId] as const,

    /**
     * Prefix shared by every projects-list cache entry regardless of filters. Invalidate with
     * this, not `projects()` — Pinia Colada's non-exact match is element-by-element, so
     * `projects(brandId)` (whose filters element normalizes to `{}`) does not match a cache
     * entry keyed with an actual search term.
     */
    projectsList: (brandId: number | null) =>
      ['constellation-pro', 'projects', brandId, 'list'] as const,

    /** Projects list for a brand, sorted by last message time, optionally filtered by search */
    projects: (brandId: number | null, filters: Record<string, unknown> = {}) =>
      [...QueryKeys.constellationPro.projectsList(brandId), normalizeFilters(filters)] as const,

    /**
     * Root key for one project's detail, messages and assets. The list lives outside this
     * prefix so invalidating a project doesn't refetch every mounted chat via the list.
     */
    projectRoot: (brandId: number | null, projectId: number | null) =>
      ['constellation-pro', 'projects', brandId, projectId] as const,

    /** Project detail, including its chats */
    project: (brandId: number | null, projectId: number | null) =>
      ['constellation-pro', 'projects', brandId, projectId, 'detail'] as const,

    /**
     * One chat's message history. Nested project → chat → messages so a socket patch
     * invalidates exactly one chat and leaves sibling chat caches untouched.
     */
    chatMessages: (brandId: number | null, projectId: number | null, chatId: number | null) =>
      ['constellation-pro', 'projects', brandId, projectId, 'chats', chatId, 'messages'] as const,

    /**
     * Prefix shared by every filter variant of one project's assets. Invalidate/patch with
     * this, not `projectAssets()` — Colada's non-exact match is element-by-element, so a
     * normalized-empty-filters key would miss an entry keyed by an actual role filter.
     */
    projectAssetsRoot: (brandId: number | null, projectId: number | null) =>
      [...QueryKeys.constellationPro.projectRoot(brandId, projectId), 'assets'] as const,

    /** Assets attached to one project, filterable by role/chat */
    projectAssets: (
      brandId: number | null,
      projectId: number | null,
      filters: Record<string, unknown> = {},
    ) =>
      [
        ...QueryKeys.constellationPro.projectAssetsRoot(brandId, projectId),
        normalizeFilters(filters),
      ] as const,

    /** Brand-user-wide AI asset library, filterable by role/type/favourite */
    aiAssets: (brandId: number | null, filters: Record<string, unknown> = {}) =>
      ['constellation-pro', 'ai-assets', brandId, normalizeFilters(filters)] as const,
  },

  /**
   * Dashboards query keys.
   * Scoped by brand-level identifiers.
   */
  dashboards: {
    /** Root key for all dashboard queries */
    all: () => ['dashboards'] as const,

    /** KAWO embed URL for a specific brand */
    kawoEmbed: (kawoBrandId: string) => ['dashboards', 'kawo-embed', kawoBrandId] as const,

    /** Metric reference metadata (display names, data types, flags) — rarely changes */
    metricDetails: () => ['dashboards', 'metric-details'] as const,

    /**
     * Channel analytics overview sections. Each section owns its own query, so more than one
     * wrapper can ask for the same data — Colada dedupes on these keys.
     */
    overview: {
      /** Root key for all overview queries */
      all: () => ['dashboards', 'overview'] as const,

      /**
       * Top-line summary stats for a channel's overview header. `metrics` is part of the key
       * because it's part of the request — see `timeseries` below.
       */
      topLine: (channel: string, brandId: number, range: OverviewKeyRange, metrics: string[]) =>
        [
          'dashboards',
          'overview',
          'top-line',
          channel,
          brandId,
          normalizeFilters(range),
          sortMetrics(metrics),
        ] as const,

      /**
       * Per-metric timeseries backing the overview charts. Two sections on the same
       * channel/brand/window/scale can request different metric sets — a followers chart and an
       * account-performance chart — so the set has to be in the key. Without it they resolve to
       * one entry and the second section reads a bag holding none of its series.
       */
      timeseries: (
        channel: string,
        brandId: number,
        range: OverviewKeyRange,
        scale: string,
        metrics: string[],
      ) =>
        [
          'dashboards',
          'overview',
          'timeseries',
          channel,
          brandId,
          normalizeFilters(range),
          scale,
          sortMetrics(metrics),
        ] as const,

      /**
       * A content section issues two requests against the same source and sort metric: the
       * aggregate drawn as the average line, and the posts themselves. They are keyed apart
       * because they resolve to different shapes — a number and a media list — and a shared key
       * would hand one section the other's payload.
       */
      content: {
        /** Aggregate stats for a content section, scoped to its source and sort metric */
        stats: (
          channel: string,
          brandId: number,
          range: OverviewKeyRange,
          source: string,
          sort: string,
        ) =>
          [
            'dashboards',
            'overview',
            'content',
            'stats',
            channel,
            brandId,
            normalizeFilters(range),
            source,
            sort,
          ] as const,

        /**
         * Posts backing a content section. Carries `sortOrder` and `limit` on top of the stats
         * key's dimensions: both change which posts come back, while neither moves the period
         * average the stats query returns.
         */
        media: (
          channel: string,
          brandId: number,
          range: OverviewKeyRange,
          source: string,
          sort: string,
          sortOrder: string,
          limit: number,
        ) =>
          [
            'dashboards',
            'overview',
            'content',
            'media',
            channel,
            brandId,
            normalizeFilters(range),
            source,
            sort,
            sortOrder,
            limit,
          ] as const,
      },
    },
  },

  overviewDemographics: {
    all: () => ['overview-demographics'] as const,

    channel: (channel: string, brandId: number | undefined, date: string | undefined) =>
      ['overview-demographics', channel, brandId ?? null, date ?? null] as const,
  },

  /**
   * Landing page query keys.
   * Scoped by brand ID.
   */
  lp: {
    /** Root key for all landing page queries */
    all: () => ['lp'] as const,

    /** Benchmark graph stats for TSI benchmarks */
    benchmarkGraphStats: (
      brandId: number,
      dateRange: { startDate: string; endDate: string },
      channels: string[],
    ) => ['lp', 'benchmark-graph-stats', brandId, dateRange, channels] as const,
  },

  /**
   * TikTok query keys.
   * Scoped by brand ID.
   */
  tiktok: {
    /** Root key for all TikTok queries */
    all: () => ['tiktok'] as const,

    /** Trending sounds with filters */
    trendingSounds: (
      brandId: number,
      filters: { dateRange: string; country: string; genre: string },
    ) => ['tiktok', 'trending-sounds', brandId, filters] as const,

    /**
     * Trending videos for a window. Outside `QueryKeys.dashboards` because tiktok-backend serves
     * it. No sort dimension — the endpoint ranks server-side and takes no sort parameter.
     */
    trendingVideos: (
      brandId: number,
      params: { startDate: string; endDate: string; limit: number },
    ) => ['tiktok', 'trending-videos', brandId, normalizeFilters(params)] as const,

    /** Follower demographics as of a single day, not a window */
    followerDemographics: (brandId: number, date: string) =>
      ['tiktok', 'follower-demographics', brandId, date] as const,
  },

  /**
   * YouTube query keys.
   */
  youtube: {
    /** Root key for all YouTube queries */
    all: () => ['youtube'] as const,

    /** Parent key for all channel-info entries — used to filter queryCache by prefix. */
    channelInfo: () => ['youtube', 'channel-info'] as const,

    /** Channel info by source channel ID — used to resolve the channel handle (custom_url). */
    channelInfoBySourceId: (sourceChannelId: string) =>
      ['youtube', 'channel-info', sourceChannelId] as const,

    /**
     * Trending videos for a window. Outside `QueryKeys.dashboards` because youtube-backend serves
     * it. No sort dimension — the endpoint ranks server-side and takes no sort parameter.
     */
    trendingVideos: (
      brandId: number,
      params: { startDate: string; endDate: string; limit: number },
    ) => ['youtube', 'trending-videos', brandId, normalizeFilters(params)] as const,
  },

  /**
   * Community query keys.
   * Scoped by brand ID(s) and filter payload.
   */
  community: {
    /** Root key for all community queries */
    all: () => ['community'] as const,

    /** Team Activity rollup (POST /team_activities) keyed by full filter payload */
    teamActivities: (payload: Record<string, unknown>) =>
      ['community', 'team-activities', payload] as const,

    /** Resolve a brand's INSTAGRAM_CONVERSATION row for a given creator handle (exact match) */
    dmByHandle: (brandId: number, handle: string) =>
      ['community', 'dm-by-handle', brandId, handle] as const,

    /** AI prompt categories that a brand has set up to use in filters */
    aiCategories: (brandId: number) => ['community', 'ai-categories', brandId] as const,

    /** Saved replies for one brand */
    presetReplies: (brandId: number) => ['community', 'preset-replies', brandId] as const,

    /** Message tags across brands (GET /tags), used to resolve tag names */
    messageTags: (brandIds: number[]) => ['community', 'message-tags', brandIds] as const,

    /** Assignable users across brands (GET /assignees) */
    assignees: (brandIds: number[]) => ['community', 'assignees', brandIds] as const,

    /** Sample of interactions matching a draft custom view's filters */
    interactionsPreview: (params: Record<string, unknown>) =>
      ['community', 'interactions-preview', params] as const,
  },

  /**
   * Release notes query keys.
   * Global (not scoped by brand).
   */
  releaseNotes: {
    /** Root key for all release notes queries */
    all: () => ['release-notes'] as const,

    /** Release notes list */
    list: () => ['release-notes', 'list'] as const,
  },

  /**
   * Video generation query keys.
   * Scoped by brand ID.
   */
  videoGeneration: {
    /** Root key for all video generation queries */
    all: () => ['video-generation'] as const,

    /** Usage stats for a brand */
    usage: (brandId: number) => ['video-generation', 'usage', brandId] as const,
  },

  /**
   * Information Request Form query keys.
   * Scoped by token.
   */
  informationRequest: {
    /** Root key for all IRF queries */
    all: () => ['information-request'] as const,

    /** Form data for a specific token */
    form: (token: string) => ['information-request', 'form', token] as const,
  },

  /**
   * Insights query keys.
   * Brand-scoped for the insight chip; organization-scoped for social listening.
   */
  insights: {
    /** Root key for all insights queries */
    all: () => ['insights'] as const,

    /** Active insights list for one or more brands */
    list: (brandIds: number[]) =>
      ['insights', 'list', [...brandIds].sort((a, b) => a - b)] as const,

    /** Active insights list for one organization */
    listByOrganization: (organizationId: number) =>
      ['insights', 'list-by-organization', organizationId] as const,
  },

  /**
   * Partnership DMs query keys.
   * Scoped by brand, source, and creator's source user id.
   */
  partnerships: {
    /** Root key for all partnership queries */
    all: () => ['partnerships'] as const,

    /** Thread for a creator (without cursor — pagination is via infinite query pages) */
    messages: (brandId: number, source: string, sourceCreatorId: string) =>
      ['partnerships', 'messages', brandId, source, sourceCreatorId] as const,

    /** Conversation metadata (PARTNERSHIP + GENERAL DM rows) for a creator */
    conversations: (brandId: number, source: string, sourceCreatorId: string) =>
      ['partnerships', 'conversations', brandId, source, sourceCreatorId] as const,

    /** Message templates for outreach (scoped to brand + source) */
    messageTemplates: (brandId: number, source: string) =>
      ['partnerships', 'message-templates', brandId, source] as const,
  },

  /**
   * Products query keys (product store migration).
   * All product data is brand-scoped — brand id in every key replaces the
   * old store's `resetOnBrandChange: true`.
   */
  products: {
    /** Root key for all products queries — use for broad invalidation */
    all: () => ['products'] as const,

    /** Root for all product-list pages of a brand (cache walk / invalidation) */
    listRoot: (brandId: number) => ['products', 'list', brandId] as const,

    /**
     * Paginated product list for a brand.
     * `filters` is normalized so equivalent payloads share a cache entry.
     */
    list: (brandId: number, filters: Record<string, unknown>) =>
      ['products', 'list', brandId, normalizeFilters(filters)] as const,

    /**
     * Brand-has-any-products probe (`limit: 1` existence check).
     * Lives under `listRoot` so list invalidation refreshes it, but the
     * string discriminator can never collide with a `list` filters object.
     */
    hasProducts: (brandId: number) => ['products', 'list', brandId, 'has-products'] as const,

    /** Single product detail */
    detail: (brandId: number, productId: number | string) =>
      ['products', 'detail', brandId, productId] as const,

    /** Product feeds for a brand, keyed by request params (sort) */
    feeds: (brandId: number, params: Record<string, unknown> = {}) =>
      ['products', 'feeds', brandId, normalizeFilters(params)] as const,

    /** Brand-level products click stats (scale/date range) */
    analytics: (brandId: number, params: Record<string, unknown>) =>
      ['products', 'analytics', brandId, normalizeFilters(params)] as const,

    /** Per-product click stats (scale/date range/source) */
    clicks: (brandId: number, productId: number | string, params: Record<string, unknown>) =>
      ['products', 'clicks', brandId, productId, normalizeFilters(params)] as const,

    /** Media attached to a product */
    media: (brandId: number, productId: number | string) =>
      ['products', 'media', brandId, productId] as const,

    /** Open Graph metadata lookups made from the product link editor */
    urlMetadata: (brandId: number, url: string) =>
      ['products', 'url-metadata', brandId, url] as const,
  },

  /**
   * Intuitive Topic Creation (NLS) query keys.
   * Scoped by organization ID.
   */
  intuitiveTopicCreation: {
    /** Root key for all intuitive topic creation queries */
    all: () => ['intuitive-topic-creation'] as const,

    /** Example prompts for an organization */
    examplePrompts: (orgId: number) =>
      ['intuitive-topic-creation', 'example-prompts', orgId] as const,

    /** Batch stats for all trending topic cards in a section (daily cache) */
    trendingTopicAllCardStats: (orgId: number, keywordsExpressions: string[], date: string) =>
      [
        'intuitive-topic-creation',
        'trending-topic-all-card-stats',
        orgId,
        keywordsExpressions,
        date,
      ] as const,
  },

  /**
   * Creator portal queries.
   * Scoped by the creator's long-lived portal link token — there is no brand or user in scope.
   */
  creatorPortal: {
    /** Root key for all creator portal queries */
    all: () => ['creator-portal'] as const,

    /** One bucket (todo / completed / expired) of the creator's gifting campaigns */
    giftingCampaigns: (token: string, type: string) =>
      ['creator-portal', 'gifting-campaigns', token, type] as const,

    /** Every bucket of a creator's gifting campaigns, for invalidating all of them at once */
    giftingCampaignsAll: (token: string) => ['creator-portal', 'gifting-campaigns', token] as const,

    /** A campaign's gifting collections and their selectable products */
    campaignCollections: (token: string, campaignId: number) =>
      ['creator-portal', 'gifting-collections', token, campaignId] as const,

    /** The creator's stored order for one gifting campaign */
    giftingCampaignOrder: (token: string, campaignId: number) =>
      ['creator-portal', 'gifting-campaign-order', token, campaignId] as const,

    /** The creator's stored shipping details, used to pre-fill the shipping form */
    shippingDetails: (token: string) => ['creator-portal', 'shipping-details', token] as const,
  },

  /** OCAPI (Salesforce Commerce Cloud) gifting integration queries. */
  ocapi: {
    /** Root key for all OCAPI queries */
    all: () => ['ocapi'] as const,

    /** Status of a brand's Data API and Shop API connections */
    connection: (brandId: number) => ['ocapi', 'connection', brandId] as const,

    /** The brand's SFCC category taxonomy (our DB copy, not a live catalog read) */
    categories: (brandId: number) => ['ocapi', 'categories', brandId] as const,

    /**
     * Paginated SFCC product search.
     * `filters` is key-sorted with `undefined` dropped; `null` is kept, so pass
     * `undefined` for an absent filter.
     */
    productSearch: (brandId: number, filters: Record<string, unknown>) =>
      ['ocapi', 'product-search', brandId, normalizeFilters(filters)] as const,

    /** A master product with its variation groups and variants */
    product: (brandId: number, productId: string) =>
      ['ocapi', 'product', brandId, productId] as const,
  },
} as const;

/**
 * Type helper to extract the return type of a query key factory.
 * Useful for typing query key parameters.
 *
 * @example
 * type TopicStatsKey = QueryKeyType<typeof QueryKeys.sl.topicStats>;
 * // readonly ['sl', 'topic-stats', number, number, Record<string, unknown>]
 */
export type QueryKeyType<T extends (...args: never[]) => readonly unknown[]> = ReturnType<T>;
